import React from 'react';
import { IonPage, IonContent } from '@ionic/react';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import ProjectCard from '@/components/ProjectCard';
import CTASection from '@/components/CTASection';
import AnimatedSection from '@/components/AnimatedSection';
import { projects } from '@/data/projects';
import styles from './Work.module.scss';

const Work: React.FC = () => {
  return (
    <IonPage id="work-page">
      <IonContent fullscreen scrollY scrollEvents={true}>
        <div className="page-wrapper">
          <Navbar />

          {/* Hero */}
          <section className={`${styles.hero} dark-section`}>
            <div className="container">
              <AnimatedSection>
                <span className={styles.eyebrow}>Our Work</span>
              </AnimatedSection>
              <AnimatedSection delay={0.08}>
                <h1 className={styles.heroTitle}>
                  Selected <span className={styles.gradient}>Work</span>
                </h1>
              </AnimatedSection>
              <AnimatedSection delay={0.16}>
                <p className={styles.heroSub}>
                  Real projects. Real solutions. This page will showcase our work as we continue building digital products for businesses worldwide.
                </p>
              </AnimatedSection>
            </div>
          </section>

          {/* Projects Gallery */}
          <section className={`section ${styles.workSection} light-section`} id="work-gallery">
            <div className={styles.galleryContainer}>
              {projects.length > 0 ? (
                <div className={styles.masonryGrid}>
                  {projects.map((project, i) => (
                    <div key={project.id} className={styles.masonryItem}>
                      <ProjectCard project={project} index={i} delay={i * 0.05} />
                    </div>
                  ))}
                </div>
              ) : (
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6 }}
                  style={{ textAlign: 'center', padding: '6rem 2rem' }}
                >
                  <h2 style={{ fontSize: '1.5rem', fontWeight: 600, marginBottom: '1rem', letterSpacing: '0.04em' }}>
                    Selected work is being updated.
                  </h2>
                  <p style={{ fontSize: '1rem', opacity: 0.6, maxWidth: '480px', margin: '0 auto 2rem', lineHeight: 1.6 }}>
                    We're preparing project showcases with real details, technology used, and outcomes. Check back soon or get in touch to discuss your project.
                  </p>
                  <Link
                    to="/contact"
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      fontSize: '0.85rem',
                      fontWeight: 600,
                      letterSpacing: '0.1em',
                      textTransform: 'uppercase' as const,
                      color: 'var(--color-accent, #3B82F6)',
                      textDecoration: 'none',
                    }}
                  >
                    START A PROJECT <ArrowRight size={16} />
                  </Link>
                </motion.div>
              )}
            </div>
          </section>

          <CTASection
            eyebrow="Your Project Next?"
            title="Let's Build Something Meaningful"
            subtitle="Tell us about your idea, project, or business challenge."
            primaryLabel="Start a Conversation"
            primaryTo="/contact"
            secondaryLabel="Our Services"
            secondaryTo="/services"
          />

          <Footer />
        </div>
      </IonContent>
    </IonPage>
  );
};

export default Work;
